import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { logger } from '@/services/loggerService'

// Shuttle ladder shared by the J/K/L transport keys. Negative = reverse.
// Capped at 8x — Chrome throws on playbackRate > 16 and audio is useless
// well before that anyway.
const SHUTTLE_LADDER = [-8, -4, -2, -1, 1, 2, 4, 8]
const DEFAULT_FPS = 30
// rAF stops firing in hidden tabs; clamp the reverse-loop delta so returning
// to the tab doesn't jump the playhead back by the whole hidden duration.
const MAX_REVERSE_TICK_SECONDS = 0.1

/**
 * J/K/L shuttle transport + frame stepping for a native <video> element.
 *
 * - K / Space: play/pause toggle. Pausing resets the shuttle so the next
 *   play resumes at normal speed.
 * - L: step up the speed ladder (1x -> 2x -> 4x -> 8x). From reverse it
 *   steps back toward forward — the opposite key always slows you down
 *   one rung rather than flipping direction.
 * - J: mirror of L toward reverse. Native <video> can't play backwards,
 *   so reverse is emulated by stepping currentTime down each animation
 *   frame while the element stays paused.
 * - , / . (and optionally Arrow Left/Right): step a single frame while
 *   paused, using the provided fps (defaults to 30 when unknown).
 *
 * @param {Function} getVideo - returns the HTMLVideoElement, or null when
 *   keyboard control should be inactive (image item, load error, etc.)
 * @param {Object} options
 * @param {boolean} [options.arrowFrameStep=false] - also bind ArrowLeft/Right
 *   to frame stepping. Leave off where arrows already navigate between items.
 * @param {Function} [options.getFps] - returns the video frame rate
 * @param {Function} [options.getBaseRate] - returns the playback rate to
 *   restore when leaving shuttle mode (e.g. the user's selected speed)
 */
export function useVideoShuttle(getVideo, options = {}) {
  const {
    arrowFrameStep = false,
    getFps = () => DEFAULT_FPS,
    getBaseRate = () => 1,
  } = options

  // 0 = neutral (no shuttle override — normal play or paused)
  const shuttleSpeed = ref(0)
  const isShuttling = computed(() => shuttleSpeed.value !== 0)
  const isReversing = computed(() => shuttleSpeed.value < 0)

  let rafId = null
  let lastTick = 0

  function safePlay(video) {
    video.play().catch((err) => {
      if (err.name !== 'AbortError') {
        logger.warn('[useVideoShuttle] Play failed', { error: err.message })
      }
    })
  }

  function stopReverseLoop() {
    if (rafId) {
      cancelAnimationFrame(rafId)
      rafId = null
    }
  }

  function startReverseLoop() {
    stopReverseLoop()
    lastTick = performance.now()
    const tick = (now) => {
      rafId = null
      const video = getVideo()
      if (!video || shuttleSpeed.value >= 0) return
      const dt = Math.min((now - lastTick) / 1000, MAX_REVERSE_TICK_SECONDS)
      lastTick = now
      const next = video.currentTime - Math.abs(shuttleSpeed.value) * dt
      if (next <= 0) {
        video.currentTime = 0
        stopShuttle({ pause: true })
        return
      }
      video.currentTime = next
      rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
  }

  /**
   * Exit shuttle mode and restore the element's base playback rate.
   * Safe to call when not shuttling (it then only honors `pause`).
   */
  function stopShuttle({ pause = false } = {}) {
    stopReverseLoop()
    const video = getVideo()
    if (shuttleSpeed.value !== 0) {
      shuttleSpeed.value = 0
      if (video) video.playbackRate = getBaseRate()
    }
    if (pause && video && !video.paused) video.pause()
  }

  // Where we sit on the ladder right now. Normal playback counts as +1 so
  // the first L while playing bumps to 2x; paused/ended is neutral (0).
  function currentRung(video) {
    if (shuttleSpeed.value !== 0) return shuttleSpeed.value
    return video.paused || video.ended ? 0 : 1
  }

  function applyRung(rung) {
    const video = getVideo()
    if (!video) return
    if (rung > 0) {
      stopReverseLoop()
      if (rung === 1) {
        // +1 is just normal playback — leave shuttle mode entirely so the
        // user's selected speed applies again.
        shuttleSpeed.value = 0
        video.playbackRate = getBaseRate()
      } else {
        shuttleSpeed.value = rung
        video.playbackRate = rung
      }
      if (video.paused) safePlay(video)
    } else {
      shuttleSpeed.value = rung
      if (!video.paused) video.pause()
      startReverseLoop()
    }
  }

  function shuttleStep(direction) {
    const video = getVideo()
    if (!video) return
    const rung = currentRung(video)
    let next
    if (rung === 0) {
      next = direction > 0 ? 1 : -1
    } else {
      const idx = SHUTTLE_LADDER.indexOf(rung)
      const nextIdx = Math.min(Math.max(idx + direction, 0), SHUTTLE_LADDER.length - 1)
      next = SHUTTLE_LADDER[nextIdx]
    }
    applyRung(next)
  }

  function togglePlayPause() {
    const video = getVideo()
    if (!video) return
    if (isShuttling.value) {
      // K during shuttle is a hard stop — next play resumes at normal speed.
      stopShuttle({ pause: true })
      return
    }
    if (video.paused || video.ended) {
      video.playbackRate = getBaseRate()
      safePlay(video)
    } else {
      video.pause()
    }
  }

  function frameStep(direction) {
    const video = getVideo()
    if (!video || !Number.isFinite(video.duration)) return
    // Stepping only makes sense from a standstill — stop any motion first.
    stopShuttle({ pause: true })
    const fps = getFps() || DEFAULT_FPS
    const next = Math.min(Math.max(video.currentTime + direction / fps, 0), video.duration)
    video.currentTime = next
  }

  function isTypingTarget(target) {
    if (!target) return false
    const tag = target.tagName
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable
  }

  function handleKeydown(event) {
    if (event.metaKey || event.ctrlKey || event.altKey || event.isComposing) return
    if (isTypingTarget(event.target)) return
    if (!getVideo()) return

    let handled = true
    switch (event.key) {
      case 'j':
      case 'J':
        // Ignore key auto-repeat for transport keys — holding J/L shouldn't
        // race up the ladder. Frame stepping below allows repeat on purpose.
        if (!event.repeat) shuttleStep(-1)
        break
      case 'k':
      case 'K':
        if (!event.repeat) togglePlayPause()
        break
      case ' ':
        // A focused button already consumes Space as a click — toggling here
        // too would double-fire (e.g. the timeline's play button).
        if (event.target?.tagName === 'BUTTON') {
          handled = false
          break
        }
        if (!event.repeat) togglePlayPause()
        break
      case 'l':
      case 'L':
        if (!event.repeat) shuttleStep(1)
        break
      case ',':
        frameStep(-1)
        break
      case '.':
        frameStep(1)
        break
      case 'ArrowLeft':
        if (arrowFrameStep) frameStep(-1)
        else handled = false
        break
      case 'ArrowRight':
        if (arrowFrameStep) frameStep(1)
        else handled = false
        break
      default:
        handled = false
    }
    if (handled) event.preventDefault()
  }

  onMounted(() => {
    window.addEventListener('keydown', handleKeydown)
  })

  onBeforeUnmount(() => {
    stopReverseLoop()
    window.removeEventListener('keydown', handleKeydown)
  })

  return { shuttleSpeed, isShuttling, isReversing, stopShuttle }
}
